/**
 * /import/:breedSlug/from/:country — Programmatic SEO landing.
 * Renders kennels from the Inbound Intl Breeder Directory matching the
 * breed + country. Empty result still renders a useful page with a
 * "Post a WTB request" CTA + transport cross-sell.
 */
import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, Link } from "react-router-dom";
import PublicShell from "@/components/PublicShell";
import AppShell from "@/components/AppShell";
import SeoHead from "@/components/SeoHead";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { GlobeHemisphereWestIcon as GlobeHemisphereWest, AirplaneTiltIcon as AirplaneTilt, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

export default function ImportBreedFromCountry() {
  const { breedSlug, country } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/seo/import/${breedSlug}/from/${country}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [breedSlug, country]);

  const Shell = user ? AppShell : PublicShell;
  if (loading) return <Shell><div className="max-w-3xl mx-auto p-10 text-sm text-[#6B6B6B]">Loading…</div></Shell>;
  if (!data) return <Shell><div className="max-w-3xl mx-auto p-10 text-center"><p>Page not found.</p><Link to="/intl-breeders" className="text-crimson underline">Browse all intl kennels</Link></div></Shell>;

  const breedDisplay = data.breed;
  const countryDisplay = data.country;
  const title = `Import ${breedDisplay} from ${countryDisplay} · Armstrong K9 Registry`;
  const description = `Find ${countryDisplay} kennels offering ${breedDisplay} dogs to US buyers. Email-relay handoff — Armstrong is the venue, no escrow or transport coordination.`;

  return (
    <Shell>
      <SeoHead
        title={title}
        description={description}
        url={`${webOrigin()}/import/${breedSlug}/from/${country.toLowerCase()}`}
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": title,
          "description": description,
          "url": `${webOrigin()}/import/${breedSlug}/from/${country.toLowerCase()}`,
        }}
      />
      <div className="max-w-5xl mx-auto px-4 py-10" data-testid="seo-import-page">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
          Import {breedDisplay} → US
        </p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Import {breedDisplay} from {countryDisplay}
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          {countryDisplay} kennels offering {breedDisplay} dogs to US buyers,
          listed via the Armstrong K9 Registry Inbound Directory. Email relay
          only — you negotiate, contract, and arrange transport directly with
          the kennel.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-8 mt-8">
          <section>
            {data.breeders.length === 0 ? (
              <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl p-8" data-testid="seo-import-empty">
                <p className="font-semibold mb-2">No {breedDisplay} kennels in {countryDisplay} are listed yet.</p>
                <p className="text-sm text-[#6B6B6B] leading-relaxed">
                  Two options that work today:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-[#3F3F3F]">
                  <li>1. <Link to="/marketplace/wtb" className="text-crimson underline font-bold">Post a Wanted-To-Buy request</Link> — foreign breeders pay to reply with kennel + photos.</li>
                  <li>2. <Link to="/intl-breeders" className="text-crimson underline font-bold">Browse all intl kennels</Link> across every breed.</li>
                </ul>
              </div>
            ) : (
              <div className="space-y-3">
                {data.breeders.map((b) => (
                  <Link
                    key={b.id}
                    to={`/intl-breeders/${b.id}`}
                    className="block bg-white border border-[#EAEAEA] rounded-xl p-5 hover:border-gold/55 hover:-translate-y-0.5 hover:shadow-gold-glow transition-all"
                    data-testid={`seo-import-card-${b.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <h3 className="font-spectral text-lg tracking-tight leading-tight flex-1">{b.kennel_name}</h3>
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] flex items-center gap-1">
                        <GlobeHemisphereWest size={12} weight="duotone"/>{b.country}
                      </span>
                    </div>
                    {b.city && <p className="text-[12px] text-[#6B6B6B]">{b.city}</p>}
                    {b.bio && <p className="text-[12.5px] text-[#6B6B6B] mt-2 line-clamp-2">{b.bio}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {b.intl_shipping_supported && <span className="bg-[#EFF7EE] border border-[#0F8043]/40 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#0F8043]">Ships intl</span>}
                      {b.breeding_rights_offered && <span className="bg-[#FBF7EC] border border-[#C9A227] rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A6A00]">Breeding rights</span>}
                    </div>
                    <span className="inline-flex items-center gap-1 text-[12px] text-crimson font-bold mt-3">
                      Contact kennel <ArrowRight size={11} weight="bold"/>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>
          <aside className="space-y-4">
            <Link
              to={data.transport_route_url}
              className="block rounded-xl border border-[#E8E0CC] bg-cream p-4 hover:border-gold transition-colors"
              data-testid="seo-import-courier-link"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8A6A00] mb-2 flex items-center gap-1.5">
                <AirplaneTilt size={11} weight="duotone"/> Transport · {countryDisplay} → US
              </p>
              <p className="text-[13px] leading-relaxed">
                Need a courier? Browse verified international couriers running this route.
              </p>
              <span className="text-[12px] font-bold text-crimson mt-2 inline-block">See couriers →</span>
            </Link>
            <Link
              to="/marketplace/wtb"
              className="block rounded-xl border border-[#E5E5E5] bg-white p-4 hover:border-gold transition-colors"
              data-testid="seo-import-wtb-link"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#6B6B6B] mb-2">Wanted-To-Buy Board</p>
              <p className="text-[13px] leading-relaxed">
                Post a WTB request — foreign breeders reply with photos and pricing for $9.
              </p>
              <span className="text-[12px] font-bold text-crimson mt-2 inline-block">Post a request →</span>
            </Link>
            <div className="rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] p-4 text-[11.5px] text-[#6B6B6B] leading-relaxed">
              <strong className="text-ink">Venue notice:</strong> Armstrong K9 Registry does not screen kennels, verify pedigrees, hold deposits, or coordinate international transport. Any agreement is between you and the kennel directly.
            </div>
          </aside>
        </div>
      </div>
    </Shell>
  );
}
